import { MonetaryAmount, Shimmer } from '@components'
import { AccountingReportRow, ReportColumnUnit, ReportRequestBody } from '@query'
import { formatPercent } from '@utils'
import cn from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronDown, FaChevronRight } from 'react-icons/fa'
import styled from 'styled-components'
import { LedgerAccountEntriesPanel } from './LedgerAccountEntriesPanel'
import { PeriodColumnHeaders, type ColumnControls } from './ColumnHeaderMenu'
import {
  Caret,
  ColumnHeaderRow,
  InlineAccountCode,
  LineItemHeader,
  ReportRow,
  RowLabel,
  RowLabelText,
  ValueCell,
  AddColumnSpacer
} from './interactive.styled'
import type { ResolvedPeriod } from './periodUtils'
import {
  ACCOUNTING_VALUE_COLUMN_WIDTH,
  accountingTableMinWidthPx,
  buildLedgerDrillColumns,
  DRILL_COLUMN_VALUE_INDEX,
  hierarchyLabelIndentFrom
} from './periodUtils'
import { ReportCardLayout } from './ReportCardLayout'
import { resolveChangeColumnLabel, type AccountingReportDisplayJson } from './optimisticReportData'
import type { ReportExpansion } from './interactiveReportsSession'
import { useOnTokenIncrement } from './useOnTokenIncrement'

type Props = {
  slug: string
  data: AccountingReportDisplayJson
  /**
   * Window the account drill lists entries over. On the balance sheet this reaches
   * back to inception, so it is wider than the column the figures are labelled with.
   */
  drillPeriod: ResolvedPeriod
  columnControls: ColumnControls
  tagIds: number[]
  expansion: ReportExpansion
  onExpansionChange: (patch: Partial<ReportExpansion>) => void
  expandAllToken: number
  collapseAllToken: number
  currency?: string
  title: string
  footNote: string
  isRefreshing?: boolean
  isStale?: boolean
}

type FlatNode =
  | {
      kind: 'row'
      id: string
      index: number
      name: string
      level: number
      isSum: boolean
      values: (number | null)[]
      hasSectionChildren: boolean
      hasAccounts: boolean
    }
  | {
      kind: 'account'
      id: string
      number: string
      name: string
      level: number
      values: (number | null)[]
      parentRowId: string
    }

const ReportValue: React.FC<{
  value: number | null
  currency?: string
  strong?: boolean
  isColumnDataPending?: boolean
  showChangeColor?: boolean
  highlighted?: boolean
  unit: ReportColumnUnit | undefined
}> = ({
  value,
  currency,
  strong,
  isColumnDataPending = false,
  showChangeColor = false,
  highlighted = false,
  unit
}) => {
  const isPositive = value != null && value > 0 && showChangeColor
  const isNegative = value != null && value < 0 && showChangeColor
  const isNeutral = value != null && value === 0 && showChangeColor

  return (
    <ValueCell
      $width={ACCOUNTING_VALUE_COLUMN_WIDTH}
      $strong={strong}
      $muted={value == null && !isColumnDataPending}
      $highlighted={highlighted}
    >
      {value != null ? (
        unit === 'percent' ? (
          <PercentFigure className={cn({ isPositive, isNegative, isNeutral })}>
            {`${formatPercent(value)} %`}
          </PercentFigure>
        ) : (
          <MonetaryAmount value={value} currency={currency} showColor={showChangeColor} />
        )
      ) : isColumnDataPending ? (
        <Shimmer width={64} height={12} borderRadius={4} style={{ display: 'inline-block' }} />
      ) : null}
    </ValueCell>
  )
}

const PercentFigure = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &.isPositive {
    color: ${({ theme }) => theme.colors.success};
  }

  &.isNegative {
    color: ${({ theme }) => theme.colors.dashboardNeg};
  }

  &.isNeutral {
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const rowIdFor = (index: number) => `row-${index}`

/** Inclusive end index of the contiguous block nested under `startIndex` (same/higher depth). */
const getDescendantEndExclusive = (rows: AccountingReportRow[], startIndex: number): number => {
  const parentLevel = rows[startIndex].level
  let end = startIndex + 1
  while (end < rows.length && rows[end].level > parentLevel) {
    end += 1
  }
  return end
}

const hasDeeperRows = (rows: AccountingReportRow[], startIndex: number): boolean =>
  getDescendantEndExclusive(rows, startIndex) > startIndex + 1

const filterVisibleRows = (
  rows: AccountingReportRow[],
  expanded: Record<string, boolean>
): { row: AccountingReportRow; index: number }[] => {
  const visible: { row: AccountingReportRow; index: number }[] = []
  let index = 0
  while (index < rows.length) {
    visible.push({ row: rows[index], index })
    const id = rowIdFor(index)
    const nestedEnd = getDescendantEndExclusive(rows, index)
    const hasNested = nestedEnd > index + 1
    if (hasNested && !expanded[id]) {
      index = nestedEnd
      continue
    }
    index += 1
  }
  return visible
}

export const AccountingReportTree: React.FC<Props> = ({
  slug,
  data,
  drillPeriod,
  columnControls,
  tagIds,
  expansion,
  onExpansionChange,
  expandAllToken,
  collapseAllToken,
  currency,
  title,
  footNote,
  isRefreshing = false,
  isStale = false
}) => {
  const [t] = useTranslation()
  const { headers: expanded, items: expandedAccounts, headersReady } = expansion

  React.useEffect(() => {
    if (headersReady) return
    const next: Record<string, boolean> = {}
    data.rows.forEach((_, index) => {
      if (hasDeeperRows(data.rows, index)) {
        next[rowIdFor(index)] = true
      }
    })
    onExpansionChange({ headers: next, headersReady: true })
  }, [headersReady, data.rows, onExpansionChange])

  useOnTokenIncrement(expandAllToken, () => {
    const next: Record<string, boolean> = {}
    const accountOpen: Record<string, boolean> = {}
    data.rows.forEach((row, index) => {
      next[rowIdFor(index)] = true
      row.accounts?.forEach(account => {
        accountOpen[account.number] = true
      })
    })
    onExpansionChange({ headers: next, items: accountOpen, headersReady: true })
  })

  useOnTokenIncrement(collapseAllToken, () => {
    onExpansionChange({ headers: {}, items: {}, headersReady: true })
  })

  const ledgerDrillBody: ReportRequestBody = useMemo(
    () => ({
      columns: buildLedgerDrillColumns(drillPeriod),
      tag_ids: tagIds.length ? tagIds : undefined
    }),
    [drillPeriod, tagIds]
  )

  const anyAccountOpen = Object.values(expandedAccounts).some(Boolean)

  const nodes = useMemo(() => {
    const result: FlatNode[] = []
    const visible = filterVisibleRows(data.rows, expanded)

    visible.forEach(({ row, index }) => {
      const accounts = row.accounts || []
      const rowId = rowIdFor(index)
      const sectionChildren = hasDeeperRows(data.rows, index)
      result.push({
        kind: 'row',
        id: rowId,
        index,
        name: row.name,
        level: row.level,
        isSum: row.is_sum_row,
        values: row.values,
        hasSectionChildren: sectionChildren,
        hasAccounts: accounts.length > 0
      })

      if (expanded[rowId] && accounts.length > 0) {
        accounts.forEach(account => {
          result.push({
            kind: 'account',
            id: `${rowId}/acc-${account.number}`,
            number: account.number,
            name: account.name,
            level: row.level + 1,
            values: account.values,
            parentRowId: rowId
          })
        })
      }
    })
    return result
  }, [data.rows, expanded])

  const toggleRow = (id: string) => {
    onExpansionChange({ headers: { ...expanded, [id]: !expanded[id] } })
  }

  const toggleAccount = (accountNumber: string) => {
    onExpansionChange({
      items: { ...expandedAccounts, [accountNumber]: !expandedAccounts[accountNumber] }
    })
  }

  const changeLabel = resolveChangeColumnLabel(data.labels, columnControls.columns.length, '')
  const columnUnits = data.column_units ?? []

  const renderValueCells = (nodeId: string, values: (number | null)[], strong?: boolean) =>
    values.map((value, index) => {
      const isChangeColumn = changeLabel !== '' && data.labels[index] === changeLabel
      return (
        <ReportValue
          key={`${nodeId}-${index}`}
          value={value}
          currency={currency}
          strong={strong}
          isColumnDataPending={isRefreshing}
          highlighted={anyAccountOpen && index === DRILL_COLUMN_VALUE_INDEX}
          showChangeColor={isChangeColumn}
          unit={columnUnits[index]}
        />
      )
    })

  return (
    <ReportCardLayout
      title={title}
      isRefreshing={isRefreshing}
      isStale={isStale}
      footNote={footNote}
      tableMinWidth={accountingTableMinWidthPx(data.labels.length)}
    >
      <ColumnHeaderRow>
        <LineItemHeader>{t('reports.interactive.lineItem')}</LineItemHeader>
        <PeriodColumnHeaders
          controls={columnControls}
          labels={data.labels}
          drillColumnHighlighted={anyAccountOpen}
          isStale={isStale}
        />
      </ColumnHeaderRow>

      {nodes.map(node => {
        if (node.kind === 'account') {
          const accountOpen = !!expandedAccounts[node.number]

          return (
            <React.Fragment key={node.id}>
              <ReportRow $clickable onClick={() => toggleAccount(node.number)}>
                <RowLabel $indentFrom={hierarchyLabelIndentFrom(node.level)}>
                  <Caret>{accountOpen ? <FaChevronDown /> : <FaChevronRight />}</Caret>
                  <RowLabelText>
                    <InlineAccountCode>{node.number}</InlineAccountCode>
                    {node.name}
                  </RowLabelText>
                </RowLabel>
                {renderValueCells(node.id, node.values)}
                <AddColumnSpacer />
              </ReportRow>
              {accountOpen && (
                <LedgerAccountEntriesPanel
                  slug={slug}
                  reportBody={ledgerDrillBody}
                  accountNumber={node.number}
                  currency={currency}
                />
              )}
            </React.Fragment>
          )
        }

        const open = !!expanded[node.id]
        const hasChildren = node.hasSectionChildren || node.hasAccounts
        const rowStrong = node.isSum || node.level === 0

        return (
          <ReportRow
            key={node.id}
            $strong={rowStrong}
            $clickable={hasChildren}
            $borderStrong={node.isSum}
            onClick={() => hasChildren && toggleRow(node.id)}
          >
            <RowLabel $indentFrom={hierarchyLabelIndentFrom(node.level)} $strong={rowStrong}>
              <Caret>{hasChildren ? open ? <FaChevronDown /> : <FaChevronRight /> : null}</Caret>
              <RowLabelText>{node.name}</RowLabelText>
            </RowLabel>
            {renderValueCells(node.id, node.values, rowStrong)}
            <AddColumnSpacer />
          </ReportRow>
        )
      })}
    </ReportCardLayout>
  )
}
